import React, {useEffect, useRef, useState} from "react";
import {Row, Table as TanstackTable} from "@tanstack/react-table";
import {useVirtualizer, VirtualItem, Virtualizer} from "@tanstack/react-virtual";
import {Table, TableBody, TableHeader, TableRow} from "@/shared/components/ui/table";
import {ReactTableHeader} from "@/shared/components/ReactTable/ReactTableHeader";
import {cn} from "@/shared/lib/utils";
import {useColumnDnD} from "@/shared/hooks/useColumnDnD";
import {ReactTableStateOverlay} from "@/shared/components/ReactTable/ReactTableStateOverlay";

/** Możliwe stany tabeli – na tej podstawie ReactTable pokazuje overlay (loading / empty / error) lub gotową tabelę (ready). */
export type ReactTableState = "loading" | "empty" | "error" | "ready";

/** Opcjonalne komunikaty w overlay; brak = domyślne "Brak elementów" / "Nie udało się załadować danych". */
export interface ReactTableStateMessages {
    emptyMessage?: string;
    errorMessage?: string;
}

/** Opcjonalna konfiguracja wirtualizacji (np. niestandardowa wysokość wiersza, measureElement). */
export interface ReactTableVirtualizerOptions {
    estimateSize?: (index: number) => number;
    /** Powinna zwracać zmierzoną wysokość w px. */
    measureElement?: (element: HTMLElement) => number;
    overscan?: number;
}

interface ReactTableProps<TData, TItem = Row<TData>> {
    table: TanstackTable<TData>;
    /** Dodatkowa lista elementów do wyświetlenia. Gdy brak – używane są table.getRowModel().rows (wirtualizacja wewnątrz).
     Przykład: flatRows oraz rozwijane wiersze w głównej tabeli AutoTable.tsx */
    items?: TItem[];
    /** Szerokość tabeli w px. Domyślnie table.getTotalSize(). */
    renderItem: (item: TItem, virtualItem: VirtualItem) => React.ReactNode;
    /** Aktualny stan tabeli – ustawiany przy starcie ładowania (loading), po zakończeniu (ready/empty/error). */
    state?: ReactTableState;
    /** Opcjonalne komunikaty; przy state === "error" / "empty" używane zamiast domyślnych. */
    stateMessages?: ReactTableStateMessages;
    /** Szerokość tabelki */
    totalTableWidth?: number;
    /** Wysokość całej tabeli na stronie */
    heightClassName?: string;
    outerContainerRef?: (node: HTMLDivElement | null) => void;
    tableContainerRef?: (node: HTMLDivElement | null) => void;
    /** Opcjonalnie: ref do virtualizera (scrollToIndex, measure itd.). */
    virtualizerRef?: React.RefObject<Virtualizer<HTMLDivElement, HTMLElement> | null>;
    /** Opcjonalna konfiguracja wirtualizacji (domyślnie estimateSize 40, overscan 6). */
    virtualizerOptions?: ReactTableVirtualizerOptions;
}

const DEFAULT_EMPTY_MESSAGE = "Brak elementów";
const DEFAULT_ERROR_MESSAGE = "Nie udało się załadować danych";

export function ReactTable<TData, TItem = Row<TData>>({
                                                          table,
                                                          items: itemsProp,
                                                          renderItem,
                                                          state = "ready",
                                                          stateMessages,
                                                          totalTableWidth: totalTableWidthProp,
                                                          heightClassName = "h-[80vh]",
                                                          outerContainerRef,
                                                          tableContainerRef,
                                                          virtualizerRef,
                                                          virtualizerOptions
                                                      }: ReactTableProps<TData, TItem>) {
    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const [containerWidth, setContainerWidth] = useState(0);

    const effectiveItems = itemsProp ?? (table.getRowModel().rows as TItem[]);
    const rowVirtualizer = useVirtualizer({
        count: effectiveItems.length,
        getScrollElement: () => scrollContainerRef.current,
        estimateSize: virtualizerOptions?.estimateSize ?? (() => 40),
        measureElement: virtualizerOptions?.measureElement,
        overscan: virtualizerOptions?.overscan ?? 6,
    });
    const virtualRows = rowVirtualizer.getVirtualItems();
    const totalSize = rowVirtualizer.getTotalSize();
    const totalTableWidth = totalTableWidthProp ?? table.getTotalSize();

    if (virtualizerRef) virtualizerRef.current = rowVirtualizer;

    const {moveColumn} = useColumnDnD(table);
    const effectiveTableWidth = Math.max(totalTableWidth, Math.max(containerWidth - 2, 0));

    const showOverlay = state !== "ready";
    const overlayVariant: "loading" | "empty" | "error" =
        state === "loading" ? "loading" : state === "error" ? "error" : "empty";
    const overlayMessage =
        overlayVariant === "error"
            ? (stateMessages?.errorMessage ?? DEFAULT_ERROR_MESSAGE)
            : overlayVariant === "empty"
                ? (stateMessages?.emptyMessage ?? DEFAULT_EMPTY_MESSAGE)
                : undefined;

    const contentWidth = showOverlay ? "100%" : `${Math.max(effectiveTableWidth, 100)}px`;

    useEffect(() => {
        const containerEl = scrollContainerRef.current;
        if (!containerEl) return;

        const syncWidth = () => {
            const width = containerEl.clientWidth;
            setContainerWidth((prev) => (prev === width ? prev : width));
        };

        syncWidth();

        let observer: ResizeObserver | null = null;
        if (typeof ResizeObserver !== "undefined") {
            observer = new ResizeObserver(syncWidth);
            observer.observe(containerEl);
        }

        window.addEventListener("resize", syncWidth);
        return () => {
            window.removeEventListener("resize", syncWidth);
            observer?.disconnect();
        };
    }, []);

    const tableProps = {
        style: {
            width: `${effectiveTableWidth}px`,
            tableLayout: "fixed" as const,
            overflow: "hidden",
        },
        bordercollapse: "collapse",
    };

    const setScrollContainerRef = (node: HTMLDivElement | null) => {
        scrollContainerRef.current = node;
        if (tableContainerRef) {
            tableContainerRef(node);
        }
        if (outerContainerRef) {
            outerContainerRef(node);
        }
    };

    return (
        <div
            className="flex min-h-0 w-full flex-col rounded-sm border border-border"
            style={{maxWidth: "100%", overflow: "hidden"}}
        >
            <div className={`relative min-h-0 w-full ${heightClassName}`}>
                <div
                    ref={setScrollContainerRef}
                    style={{
                        width: "100%",
                        height: "100%",
                        minHeight: 0,
                        overflowY: showOverlay ? "hidden" : "auto",
                        overflowX: showOverlay ? "hidden" : "auto",
                        position: "relative",
                    }}
                >
                    <div style={{width: contentWidth}}>
                        <div className="sticky top-0 z-10 overflow-hidden bg-neutral-50">
                            <Table {...tableProps}>
                                <TableHeader>
                                    {table.getHeaderGroups().map((headerGroup) => (
                                        <TableRow key={headerGroup.id} className="flex w-full bg-neutral-50">
                                            {headerGroup.headers.map((header, i) => (
                                                <ReactTableHeader
                                                    key={header.id}
                                                    header={header}
                                                    index={i}
                                                    moveColumn={moveColumn}
                                                />
                                            ))}
                                        </TableRow>
                                    ))}
                                </TableHeader>
                            </Table>
                        </div>

                        <Table {...tableProps}>
                            <TableBody
                                style={{
                                    height: `${totalSize}px`,
                                    width: "100%",
                                    position: "relative",
                                }}
                            >
                                {virtualRows.map((virtualItem) => {
                                    const item = effectiveItems[virtualItem.index];
                                    if (item === undefined) return null;
                                    const rendered = renderItem(item, virtualItem);
                                    if (!React.isValidElement(rendered)) return rendered;

                                    const existingStyle = (rendered.props as {
                                        style?: React.CSSProperties
                                    }).style ?? {};
                                    const existingClassName = (rendered.props as { className?: string }).className;
                                    return React.cloneElement(rendered as React.ReactElement<{
                                        style?: React.CSSProperties;
                                        className?: string
                                    }>, {
                                        className: cn("flex w-full items-center justify-start", existingClassName),
                                        style: {
                                            position: "absolute",
                                            top: 0,
                                            left: 0,
                                            width: "100%",
                                            ...existingStyle,
                                        },
                                    });
                                })}
                            </TableBody>
                        </Table>
                    </div>
                </div>

                {showOverlay && (
                    <ReactTableStateOverlay
                        variant={overlayVariant}
                        message={overlayMessage}
                    />
                )}
            </div>
        </div>
    );
}

